import { isHiddenVocabularyWord } from "../consts/hiddenVocabulary";
import { fetchFullVocabularyLevel } from "./vocabularyData";
import { kanaToRomaji } from "./kanaToRomaji";

type VocabularyWord = {
    id: string;
    word: string;
    reading?: string;
    meanings?: string[];
    partsOfSpeech?: string[];
};

/**
 * Looks up the lesson term in the same on-demand vocabulary data used by the
 * vocabulary page.  This deliberately loads only enough JLPT data to find a
 * match, rather than putting a dictionary in every lesson bundle.
 */
export type SavedWordDetails = { meaning?: string; romaji?: string };

// The app no longer lists some N5/N4 entries (src/consts/hiddenVocabulary.ts),
// but a term a learner saved must keep its meaning and romaji, so this reads the
// whole level file. Hidden entries only rank after visible ones, so a hidden
// homophone never wins over the word the learner meant.
function findMatch(words: VocabularyWord[], level: number, text: string, reading?: string): VocabularyWord | undefined {
    const visible = words.filter((word) => !isHiddenVocabularyWord(word, level));
    const hidden = words.filter((word) => isHiddenVocabularyWord(word, level));
    const ranked = [...visible, ...hidden];
    return ranked.find((word) => word.word === text && (!reading || word.reading === reading))
        ?? ranked.find((word) => word.word === text);
}

export async function lookupSavedWordDetails(text: string, reading?: string): Promise<SavedWordDetails> {
    for (const level of [5, 4, 3, 2, 1]) {
        try {
            const words = await fetchFullVocabularyLevel(level) as VocabularyWord[];
            const match = findMatch(words, level, text, reading);
            if (match) return {
                meaning: match.meanings?.slice(0, 3).join("; "),
                romaji: match.reading ? kanaToRomaji(match.reading) : undefined,
            };
        } catch {
            // A failed optional lookup must never prevent saving the lesson term.
        }
    }
    return reading ? { romaji: kanaToRomaji(reading) } : {};
}
